import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { XCircle, Edit, CheckSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { getBackendUrl } from '../../utils/api';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const MasterTemplatesContent = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    config_data: {}
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/master/templates`);
      setTemplates(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await axios.put(`${API}/master/templates/${editingTemplate.id}`, formData);
      } else {
        await axios.post(`${API}/master/templates`, formData);
      }
      setShowModal(false);
      setEditingTemplate(null);
      setFormData({ name: '', description: '', config_data: {} });
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Erro ao salvar template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Tem certeza que deseja excluir este template?')) return;
    
    try {
      await axios.delete(`${API}/master/templates/${templateId}`);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Erro ao excluir template');
    }
  };

  const openModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        description: template.description || '',
        config_data: template.config_data || {}
      });
    } else {
      setEditingTemplate(null);
      setFormData({ name: '', description: '', config_data: {} });
    }
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Templates de Configuração</h1>
          <p className="text-gray-400 mt-1">Gerencie configurações predefinidas para novas lojas</p>
        </div>
        <button onClick={() => openModal()} className="glass-button-primary">
          <CheckSquare size={18} className="mr-2" />
          Novo Template
        </button>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div key={template.id} className="glass-card">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-1">{template.name}</h3>
                <p className="text-sm text-gray-400">{template.description}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => openModal(template)}
                  className="p-2 text-blue-400 hover:bg-blue-400/20 rounded-lg transition-colors"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => handleDelete(template.id)}
                  className="p-2 text-red-400 hover:bg-red-400/20 rounded-lg transition-colors"
                >
                  <XCircle size={16} />
                </button>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-700">
              <p className="text-xs text-gray-500">
                Criado em {new Date(template.created_at).toLocaleDateString('pt-BR')}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {Object.keys(template.config_data || {}).length} configurações
              </p>
            </div>
          </div>
        ))}
      </div>

      {templates.length === 0 && (
        <div className="glass-card text-center py-12">
          <CheckSquare size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Nenhum template encontrado</h3>
          <p className="text-gray-400 mb-6">Crie templates para aplicar configurações padrão em novas lojas</p>
          <button onClick={() => openModal()} className="glass-button-primary">
            Criar Primeiro Template
          </button>
        </div>
      )}

      {/* Template Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="glass-modal max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingTemplate ? 'Editar Template' : 'Novo Template'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nome</label>
              <input
                type="text"
                required
                className="glass-input w-full"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Nome do template"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
              <textarea
                className="glass-input w-full resize-none"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descrição do template"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Configurações (JSON)
              </label>
              <textarea
                className="glass-input w-full resize-none font-mono text-sm"
                rows={8}
                value={JSON.stringify(formData.config_data, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setFormData({...formData, config_data: parsed});
                  } catch (err) {
                    // Invalid JSON, just update the text
                  }
                }}
                placeholder='{"delivery_fee": 5.00, "minimum_order": 10.00}'
              />
              <p className="text-xs text-gray-500 mt-1">
                Exemplo: {`{"delivery_fee": 5.00, "minimum_order": 10.00, "phone": "+5511999999999"}`}
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button type="submit" className="glass-button-primary">
                {editingTemplate ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ============================================================================
// MASTER ACTION LOGS CONTENT (Histórico de Ações)
// ============================================================================

